/*
* Copyright 2026 Tecnalia, Basque Research & Technology Alliance (BRTA)
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*
*  SPDX-License-Identifier: Apache-2.0
*/

import { Catalog, Dataset, normalizeDatasetOffers } from "./catalog-dto";
import { AssetDTO } from "./asset-dto";

const DATASET_RESERVED_KEYS = new Set([
    '@id',
    '@type',
    'odrl:hasPolicy',
    'dcat:distribution'
]);

function dataServicesFromCatalog(catalog: Catalog) {
    const svc = catalog['dcat:service'];
    if (svc == null) {
        return [];
    }
    return Array.isArray(svc) ? svc : [svc];
}

function resolveConnectorBaseUrl(catalog: Catalog, accessServiceId: string): string {
    const match = dataServicesFromCatalog(catalog).find((s) => s['@id'] === accessServiceId);
    if (match) {
        const url = match['dcat:endpointUrl'] ?? match['dcat:endpointURL'];
        if (typeof url === 'string' && url.length > 0) {
            return url;
        }
    }
    if (typeof catalog.originator === 'string' && catalog.originator.length > 0) {
        return catalog.originator;
    }
    return '';
}

function datasetProperties(dataset: Dataset, assetId: string): Record<string, unknown> {
    const properties: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(dataset)) {
        if (DATASET_RESERVED_KEYS.has(key)) {
            continue;
        }
        properties[key] = value;
    }
    if (properties['name'] == null || properties['name'] === '') {
        properties['name'] = assetId;
    }
    if (properties['contenttype'] == null || properties['contenttype'] === '') {
        properties['contenttype'] = 'application/octet-stream';
    }
    if (properties['version'] == null || properties['version'] === '') {
        properties['version'] = '1.0';
    }
    if (properties['id'] == null || properties['id'] === '') {
        properties['id'] = assetId;
    }
    return properties;
}

export function datasetToAssetDTO(dataset: Dataset, catalog: Catalog): AssetDTO {

    const id = dataset['@id'];

    const rawType = dataset['@type'];
    if (typeof rawType !== 'string' || !rawType) {
        throw new Error(`Dataset ${id} no tiene tipo`);
    }
    const type = rawType.startsWith('dcat:')
        ? rawType.substring(5)
        : rawType;

    const distributions = dataset['dcat:distribution'];
    if (!distributions || distributions.length === 0) {
        throw new Error(`Dataset ${id} no tiene distribución`);
    }

    const first = distributions[0];
    const accessRef = first['dcat:accessService'];
    const accessServiceId =
        accessRef && typeof accessRef === 'object' && '@id' in accessRef
            ? String((accessRef as { '@id': unknown })['@id'])
            : '';
    if (!accessServiceId) {
        throw new Error(`Dataset ${id} no tiene accessService.@id`);
    }

    const baseUrl = resolveConnectorBaseUrl(catalog, accessServiceId);

    const dataAddress = {
        '@type': 'DataAddress',
        baseUrl,
        name: 'default',
        proxyPath: '',
        proxyQueryParams: '',
        type: 'HttpData'
    };

    const distributionIds: string[] = distributions.map((dist) => {
        const formatId = dist['dct:format']?.['@id'];
        if (!formatId) {
            throw new Error(`Una distribución del dataset ${id} no tiene dct:format.@id`);
        }
        return formatId;
    });

    const assetDTO: AssetDTO = {
        '@context': {
            '@vocab': 'https://w3id.org/edc/v0.0.1/ns/'
        },
        '@id': id,
        '@type': type,
        dataAddress,
        distribution: distributionIds,
        offers: normalizeDatasetOffers(dataset['odrl:hasPolicy']),
        properties: datasetProperties(dataset, id)
    };

    return assetDTO;

}